<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          title="Asset Depreciation Process"
      />
    </div>

    <div v-if="showPreferenceAlert" class="px-2 mt-2 pb-2">
      <div class="alert alert-warning mb-0">
        <div class="alert-body p-1">
          Depreciation Interval is not set up in Company Preference. Please set the Depreciation Policy from
          Company Preference before processing depreciation.
        </div>
      </div>
    </div>

    <div v-if="hasInterval" class="px-2 mt-2">
      <div class="row gy-2">
        <div v-if="isYearly" class="col-12 col-sm-6 col-md-4">
          <label class="form-label">Financial Year (Start Year)</label>
          <v-select
              placeholder="Select Year"
              v-model="formData.interval_value"
              :options="yearOptions"
              :reduce="item => item.value"
              label="name"
          />
        </div>

        <div v-if="isQuarterly" class="col-12 col-sm-6 col-md-4">
          <label class="form-label">Quarter</label>
          <v-select
              placeholder="Select Quarter"
              v-model="formData.interval_value"
              :options="quarterOptions"
              :reduce="item => item.value"
              label="name"
          />
        </div>

        <div v-if="isMonthly" class="col-12 col-sm-6 col-md-4">
          <label class="form-label">Month</label>
          <input
              class="form-control"
              type="month"
              v-model="formData.interval_value"
          >
        </div>

        <template v-if="isDaily">
          <div class="col-12 col-sm-6 col-md-4">
            <label class="form-label">Start Date</label>
            <input
                class="form-control"
                type="date"
                v-model="formData.start_date"
            >
          </div>
          <div class="col-12 col-sm-6 col-md-4">
            <label class="form-label">End Date</label>
            <input
                class="form-control"
                type="date"
                v-model="formData.end_date"
            >
          </div>
        </template>

        <div class="col-12 col-sm-6 col-md-4 d-flex align-items-end">
          <button
              :disabled="loading"
              class="btn btn-primary"
              @click="runProcess"
          >
            Process Depreciation
          </button>
        </div>
      </div>
    </div>

    <div v-if="hasInterval" class="px-2 mt-3">
      <div class="table-responsive table-min-height">
        <table class="table table-bordered">
          <thead>
          <tr>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Type</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="run in runs" :key="run.id">
            <td>{{ run.format_start_date }}</td>
            <td>{{ run.format_end_date }}</td>
            <td>{{ run.type }}</td>
            <td>{{ run.status }}</td>
            <td>
              <button
                  :disabled="loading || run.status === 'processing'"
                  class="btn btn-sm btn-outline-primary"
                  @click="reprocessRun(run.id)"
              >
                Reprocess
              </button>
            </td>
          </tr>
          </tbody>
        </table>
        <p v-if="!hasRuns" class="text-center mt-5">No data</p>
      </div>
    </div>

    <div v-if="hasInterval" class="px-2 pb-2">
      <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import {computed, inject, onMounted, ref} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useStore} from 'vuex'
import TitleButton from '@/components/atom/TitleButton'
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import handleAssetRegister from '@/services/modules/assetRegister/assetRegister'
import handleCompany from '@/services/modules/company'

const $route = useRoute()
const $router = useRouter()
const $store = useStore()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const {processDepreciation, fetchDepreciationRuns, reprocessDepreciation} = handleAssetRegister()
const {getCompanyPreferences} = handleCompany()

const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const loading = ref(false)
const preferenceLoaded = ref(false)
const runs = ref([])
const offset = ref(20)
const interval = ref('')
const fyStartMonth = ref(1)
const formData = ref({
  company_id: $route.params.companyId,
  interval_value: '',
  start_date: '',
  end_date: ''
})

const isYearly = computed(() => interval.value === 'yearly')
const isQuarterly = computed(() => interval.value === 'quarterly')
const isMonthly = computed(() => interval.value === 'monthly')
const isDaily = computed(() => interval.value === 'daily')
const hasRuns = computed(() => runs.value.length > 0)
const hasInterval = computed(() => !!interval.value)
const showPreferenceAlert = computed(() => preferenceLoaded.value && !interval.value)

const yearOptions = computed(() => {
  const current = new Date().getFullYear()
  const list = []
  let year = current + 1
  while (year >= current - 8) {
    list.push({name: String(year), value: String(year)})
    year = year - 1
  }
  return list
})

const buildQuarterLabel = (startYear, quarter) => {
  const startIndex = (fyStartMonth.value - 1) + 3 * (quarter - 1)
  const endIndex = startIndex + 2
  const startLabel = `${monthLabels[startIndex % 12]} ${startYear + Math.floor(startIndex / 12)}`
  const endLabel = `${monthLabels[endIndex % 12]} ${startYear + Math.floor(endIndex / 12)}`
  return `Q${quarter} (${startLabel} - ${endLabel})`
}

const quarterOptions = computed(() => {
  const current = new Date().getFullYear()
  const list = []
  let year = current + 1
  while (year >= current - 8) {
    for (let quarter = 1; quarter <= 4; quarter++) {
      list.push({name: `${year} ${buildQuarterLabel(year, quarter)}`, value: `${year}-Q${quarter}`})
    }
    year = year - 1
  }
  return list
})

const getQuery = () => {
  return `?company_id=${$route.params.companyId}`
}

const getRunsQuery = () => {
  let query = `${getQuery()}&offset=${offset.value}`
  if (!$route.query.page) return query
  return `${query}&page=${$route.query.page}`
}

const getInterval = () => {
  const query = `${getQuery()}&fields=depreciation_interval,financial_year_start_month`
  return getCompanyPreferences(query)
      .then(res => {
        if (!res.data) return
        setDepreciationInterval(res.data)
        setFyStartMonth(res.data)
      })
      .finally(() => preferenceLoaded.value = true)
}

const setDepreciationInterval = (data) => {
  const item = data.find(item => item.field === 'depreciation_interval')
  if (!item) return
  interval.value = item.value
}

const setFyStartMonth = (data) => {
  const item = data.find(item => item.field === 'financial_year_start_month')
  if (!item) return
  const index = monthKeys.indexOf(String(item.value).toLowerCase())
  if (index < 0) return
  fyStartMonth.value = index + 1
}

const runProcess = () => {
  if (!hasInterval.value) {
    showError('Please set up Depreciation Interval in Company Preference')
    return
  }
  if (isDaily.value && (!formData.value.start_date || !formData.value.end_date)) {
    showError('Please select start and end date')
    return
  }
  if (!isDaily.value && !formData.value.interval_value) {
    showError('Please select the process period')
    return
  }
  loading.value = true
  processDepreciation(formData.value, getQuery())
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
        getRuns()
      })
      .finally(() => loading.value = false)
}

const getRuns = () => {
  fetchDepreciationRuns(getRunsQuery())
      .then(res => {
        if (!res.status) {
          runs.value = []
          $store.commit('resetPagination')
          return
        }
        runs.value = res.data.data
        setPagination(res.data)
      })
}

const setPagination = (data) => {
  if (!data.data.length) return
  $store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, $route.query)
  routeQuery.page = page
  await $router.push({path: $route.path, query: routeQuery})
  getRuns()
}

const reprocessRun = (processTrackerId) => {
  loading.value = true
  reprocessDepreciation(processTrackerId, getQuery())
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
        getRuns()
      })
      .finally(() => loading.value = false)
}

onMounted(() => {
  getInterval()
  getRuns()
})
</script>
